import { useState } from "react";
import { API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { FileText, Camera, Trash2, Eye, CheckSquare, X, Download } from "lucide-react";

/**
 * DocumentsSection - Documents list with multi-select and delete
 */
export default function DocumentsSection({ documents, onUpload, onDelete, onRefresh }) {
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState(new Set());

  // Toggle selection
  const toggleSelect = (docId) => {
    setSelectedDocs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  // Select all
  const selectAll = () => {
    setSelectedDocs(new Set(documents.map(d => d.document_id)));
  };

  // Delete multiple
  const handleDeleteSelected = async () => {
    if (selectedDocs.size === 0) return;
    if (!confirm(`Eliminare ${selectedDocs.size} documenti selezionati?`)) return;
    
    let deleted = 0;
    for (const docId of selectedDocs) {
      try {
        const res = await authFetch(`${API}/documents/${docId}`, { method: 'DELETE' });
        if (res.ok) deleted++;
      } catch (e) {}
    }
    
    toast.success(`${deleted} documenti eliminati`);
    setSelectedDocs(new Set());
    setSelectionMode(false);
    onRefresh?.();
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get doc type label
  const getDocTypeLabel = (type) => {
    const labels = {
      libretto: "Libretto",
      passport: "Passaporto",
      vaccine_certificate: "Certificato Vaccini",
      health_certificate: "Certificato ASL",
      prescription: "Ricetta",
      exam_result: "Esame",
      insurance: "Assicurazione",
      microchip: "Certificato Microchip",
      other: "Altro"
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-semibold text-gray-800 dark:text-gray-100">
          Documenti ({documents.length})
        </h3>
        <div className="flex items-center gap-2">
          {documents.length > 0 && (
            <Button
              size="sm"
              variant={selectionMode ? "default" : "outline"}
              onClick={() => {
                setSelectionMode(!selectionMode);
                if (selectionMode) setSelectedDocs(new Set());
              }}
              className="rounded-xl text-xs"
            >
              {selectionMode ? <X size={14} className="mr-1" /> : <CheckSquare size={14} className="mr-1" />}
              {selectionMode ? "Annulla" : "Seleziona"}
            </Button>
          )}
          
          {selectionMode && selectedDocs.size > 0 && (
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDeleteSelected}
              className="rounded-xl text-xs"
            >
              <Trash2 size={14} className="mr-1" />
              Elimina ({selectedDocs.size})
            </Button>
          )}
          
          {!selectionMode && (
            <Button size="sm" onClick={onUpload} className="bg-[#1D1D1F] dark:bg-white dark:text-black rounded-xl text-xs">
              <Camera size={14} className="mr-1" /> Carica
            </Button>
          )}
        </div>
      </div>

      {/* Documents list */}
      {documents.length === 0 ? (
        <div className="bg-white dark:bg-[#2D2D2F] rounded-2xl p-8 text-center shadow-sm">
          <FileText className="mx-auto text-gray-300 dark:text-gray-600 mb-3" size={40} />
          <p className="text-gray-500 dark:text-gray-400">Nessun documento</p>
          <Button onClick={onUpload} className="mt-4 bg-[#1D1D1F] dark:bg-white dark:text-black rounded-xl">
            <Camera size={16} className="mr-2" /> Carica documento
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map(doc => {
            const isSelected = selectedDocs.has(doc.document_id);
            
            return (
              <div 
                key={doc.document_id} 
                className={`bg-white dark:bg-[#2D2D2F] rounded-2xl p-3 shadow-sm transition-all ${
                  isSelected ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-500/10' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Selection checkbox */}
                  {selectionMode && (
                    <Checkbox 
                      checked={isSelected}
                      onCheckedChange={() => toggleSelect(doc.document_id)}
                      className="border-gray-400 dark:border-gray-500"
                    />
                  )}
                  
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <FileText className="text-blue-600 dark:text-blue-400" size={20} />
                  </div>
                  
                  {/* Info */}
                  <div 
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => !selectionMode && doc.file_url && window.open(`${API.replace('/api', '')}${doc.file_url}`, '_blank')}
                  >
                    <p className="font-semibold text-gray-800 dark:text-gray-200 text-sm truncate">
                      {doc.title || doc.display_filename || "Documento"}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {getDocTypeLabel(doc.doc_type)} • {formatDate(doc.created_at)}
                    </p>
                    {doc.ocr_completed && (
                      <span className="text-xs text-emerald-600 dark:text-emerald-400">
                        ✓ Dati estratti
                      </span>
                    )}
                  </div>
                  
                  {/* Actions */}
                  {!selectionMode && (
                    <div className="flex items-center gap-1">
                      {doc.file_url && (
                        <>
                          <button 
                            onClick={() => window.open(`${API.replace('/api', '')}${doc.file_url}`, '_blank')}
                            className="p-2 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                            title="Visualizza"
                          >
                            <Eye size={16} />
                          </button>
                          <a
                            href={`${API.replace('/api', '')}${doc.file_url}`}
                            download
                            className="p-2 text-gray-400 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors"
                            title="Scarica"
                          >
                            <Download size={16} />
                          </a>
                        </>
                      )}
                      <button 
                        onClick={() => onDelete(doc.document_id)}
                        className="p-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                        title="Elimina"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Select all button */}
      {selectionMode && documents.length > 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={selectAll}
          className="w-full rounded-xl text-xs"
        >
          Seleziona tutti ({documents.length})
        </Button>
      )}
    </div>
  );
}
